/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Outlet, useLocation } from "react-router-dom";

// icons imports
import { Filter } from "../../../assets/icons";

// Local Imports
import { PartnersIcon } from "../../../assets/icons";
import {
  Chip,
  CustomPopover,
  MobileDrawer,
  Divider,
  EmptyState,
  PartnerCard,
  PartnerCardSkeletonGrid,
  SelectDropdown,
  SignupInterestCheckbox,
  TextInput,
} from "../../../components/shared";
import { HiOutlineUserGroup } from "react-icons/hi2";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { rolesAndInterestsFunction } from "../../../redux/reducers/authReducers/rolesInterestsReducer";
import {
  citiesFunction,
  statesFunction,
} from "../../../redux/reducers/authReducers/statesReducer";
import { useFilteredPartners } from "../../../lib/react-query/partners.ts";
import { getFullName, getPartnerLocationLabel } from "../../../utils/utils";
import { NETWORK_TABS } from "./networkTabs";

// Initial Filters Data
const filtersData = {
  state: "",
  city: "",
  interests: [],
  industry: "",
  profession: "",
  skill: "",
  zipCode: "",
};

const initialFormData = {
  ...filtersData,
  search: "",
};

const Partner = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { cityLoading } = useSelector((state) => state?.states);

  const [openPopover, setOpenPopover] = useState(false);
  const [cityList, setCityList] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [appliedFilters, setAppliedFilters] = useState(filtersData);
  const [debouncedSearch, setDebouncedSearch] = useState(formData.search);
  const [formDataError, setFormDataError] = useState("");
  const [isDisabled, setIsDisabled] = useState(true);
  const isMobile = useIsMobile();

  const queryFilters = {
    ...appliedFilters,
    search: debouncedSearch,
  };

  const hasAppliedFilters = Object.values(appliedFilters).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== "",
  );

  const hasActiveFilters =
    hasAppliedFilters || debouncedSearch.trim().length > 0;

  const {
    data: partnersListing,
    isLoading: partnersLoading,
  } = useFilteredPartners(
    {
      industry: queryFilters?.industry,
      profession: queryFilters?.profession,
      city: queryFilters?.city,
      skills: [queryFilters?.skill].filter(Boolean),
      interests: queryFilters?.interests?.map(
        (interest) => interest.charAt(0).toUpperCase() + interest.slice(1),
      ),
      search: queryFilters?.search,
      postalcode: queryFilters?.zipCode,
    },
    hasActiveFilters,
  );

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    const statesResponse = await dispatch(statesFunction());
    setStatesList(statesResponse?.payload || []);
  };

  const fetchCities = async (stateId) => {
    if (!stateId) return;
    const citiesList = await dispatch(citiesFunction({ id: stateId }));
    setCityList(citiesList?.payload || []);
  };

  const handleStateChange = (event) => {
    const { value } = event.target;
    setFormData({ ...formData, state: value, city: "" });
    fetchCities(value);
  };

  const handleCityChange = (event) => {
    const { value } = event.target;
    setFormData({ ...formData, city: value });
  };

  const handleInterestsChange = (name) => {
    const updatedInterests = [...(formData.interests || [])];
    // Check if interest is already selected
    const interestIndex = updatedInterests.indexOf(name);
    // If the interest is not selected
    if (interestIndex === -1) {
      updatedInterests.push(name);
    } else {
      updatedInterests.splice(interestIndex, 1);
    }

    setFormData((prev) => ({
      ...prev,
      interests: updatedInterests,
    }));
  };

  const [interests, setInterests] = useState([]);
  const getRolesInterests = async () => {
    try {
      const rolesAndInterest = await dispatch(rolesAndInterestsFunction());
      const interestsArray = rolesAndInterest?.payload?.interests?.reduce(
        (prev, curr) => [
          ...prev,
          {
            id: curr?.id,
            name: curr?.name?.toLowerCase(),
            label: curr?.name,
            value: false,
          },
        ],
        [],
      );

      setInterests(interestsArray);
    } catch (error) {
      console.error("Error fetching roles and interests:", error);
    }
  };

  useEffect(() => {
    getRolesInterests();
  }, []);

  useEffect(() => {
    // Check if all fields are empty
    const isFormEmpty = Object.values(formData).every((value) =>
      Array.isArray(value) ? value.length === 0 : value === "",
    );

    if (isFormEmpty) {
      setIsDisabled(true);
      setFormDataError("");
      return;
    }

    if (formData.state && !formData.city) {
      setIsDisabled(true);
      setFormDataError("Please select a city to proceed with the search.");
      return;
    }

    const areOtherFieldsFilled = Object.entries(formData).some(
      ([key, value]) =>
        key !== "state" &&
        key !== "city" &&
        (Array.isArray(value) ? value.length > 0 : value !== ""),
    );

    if (!formData.state && !formData.city && !areOtherFieldsFilled) {
      setIsDisabled(true);
      setFormDataError("");
      return;
    }

    setIsDisabled(false);
    setFormDataError("");
  }, [formData]);

  const onChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const filterOnClick = () => {
    setFormDataError("");
    if (formData?.state && !formData?.city) {
      setFormDataError("Please select a city to proceed with the search.");
      return;
    }
    setOpenPopover(false);
    setAppliedFilters({
      state: formData.state,
      city: formData.city,
      interests: formData.interests,
      industry: formData.industry,
      profession: formData.profession,
      skill: formData.skill,
      zipCode: formData.zipCode,
    });
  };

  const handleClearFilter = () => {
    setOpenPopover(false);
    setFormData(initialFormData);
    setAppliedFilters(filtersData);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(formData.search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [formData.search]);

  return (
    <div className="">
      <div className="bg-bggrey pt-4 sticky top-0 z-50 rounded-b-xl">
        <div className="bg-white rounded-xl">
          <div className="flex justify-between items-center pr-5">
            <div className="font-sans vertical-center gap-4 p-2 py-3 pl-5">
              <PartnersIcon width={30} height={30} />
              <h3 className="text-xl">Network</h3>
            </div>
            {debouncedSearch ? (
              ""
            ) : isMobile ? (
              <>
                <div 
                  className="cursor-pointer flex items-center gap-1"
                  onClick={() => setOpenPopover(true)}
                >
                  <Filter />
                  <h3 className="text-lg mb-[3px]">Filter</h3>
                </div>
                <MobileDrawer
                  title="Filter Connections"
                  open={openPopover}
                  onClose={() => setOpenPopover(false)}
                >
                  <div className="flex flex-col gap-4">
                    <SelectDropdown
                      options={statesList}
                      label="Select State"
                      name="state"
                      value={formData.state}
                      onChange={handleStateChange}
                    />
                    <SelectDropdown
                      options={cityList}
                      disabled={!formData.state}
                      loading={cityLoading}
                      label="Select City"
                      name="city"
                      value={formData.city}
                      error={formDataError}
                      errorMsg={formDataError}
                      onChange={handleCityChange}
                      description={
                        cityLoading
                          ? "Loading..."
                          : !formData.state
                            ? "Please Select State First"
                            : ""
                      }
                    />
                    <div>
                      <p className="font-normal text-sm pb-1">ZIP Code</p>
                      <TextInput
                        label="Enter a ZIP Code"
                        type="text"
                        name="zipCode"
                        value={formData?.zipCode}
                        onChange={onChange}
                      />
                    </div>
                    <div>
                      <p className="font-normal text-sm pb-1">Interests</p>
                      <div className="flex flex-wrap gap-2">
                        {interests?.map((item) => {
                          const isChecked = formData?.interests.includes(item?.name);
                          return (
                            <SignupInterestCheckbox
                              key={item?.id}
                              name={item?.name}
                              value={isChecked}
                              onChange={() => handleInterestsChange(item?.name)}
                            >
                              {item?.label}
                            </SignupInterestCheckbox>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <p className="font-normal text-sm pb-1">Profession</p>
                      <TextInput
                        label="Enter a profession"
                        type="text"
                        name="profession"
                        value={formData?.profession}
                        onChange={onChange}
                      />
                    </div>
                    <div>
                      <p className="font-normal text-sm pb-1">Industry</p>
                      <TextInput
                        label="Enter an industry"
                        type="text"
                        name="industry"
                        value={formData?.industry}
                        onChange={onChange}
                      />
                    </div>
                    <div>
                      <p className="font-normal text-sm pb-1">Skill</p>
                      <TextInput
                        label="Enter a Skill"
                        type="text"
                        name="skill"
                        value={formData?.skill}
                        onChange={onChange}
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={handleClearFilter}
                        className="flex-1 py-2 px-4 rounded-lg border border-surface-border text-grey font-medium"
                      >
                        Clear
                      </button>
                      <button
                        onClick={filterOnClick}
                        disabled={isDisabled}
                        className={`flex-1 py-2 px-4 rounded-lg font-medium ${
                          isDisabled
                            ? "bg-surface-active text-mediumgrey cursor-not-allowed"
                            : "bg-primary text-white hover:bg-primary/90"
                        }`}
                      >
                        Apply
                      </button>
                    </div>
                    {formDataError && (
                      <p className="text-sm text-danger">{formDataError}</p>
                    )}
                  </div>
                </MobileDrawer>
              </>
            ) : (
              <CustomPopover
                open={openPopover}
                setOpen={setOpenPopover}
                placement="bottom-end"
                trigger={
                  <div className="cursor-pointer flex items-center gap-1">
                    <Filter />
                    <h3 className="text-lg mb-[3px]">Filter</h3>
                  </div>
                }
              >
                <div className="gap-3 w-80 flex flex-col items-center bg-white p-2 rounded-md ">
                  <div className="flex flex-col gap-3 w-full border-b border-surface-border pb-2">
                    <div className="vertical-center justify-between">
                      <p className="font-sans font-semibold text-sm">
                        Filter Options
                      </p>
                      <p
                        onClick={handleClearFilter}
                        className="font-sans cursor-pointer font-semibold text-sm text-secondary-dark"
                      >
                        Clear
                      </p>
                    </div>
                    <div className="flex flex-col gap-4 overflow-auto h-[400px]">
                      <SelectDropdown
                        options={statesList}
                        label="Select State"
                        name="state"
                        value={formData.state}
                        onChange={handleStateChange}
                      />
                      <SelectDropdown
                        options={cityList}
                        disabled={!formData.state}
                        loading={cityLoading}
                        label="Select City"
                        name="city"
                        value={formData.city}
                        error={formDataError}
                        errorMsg={formDataError}
                        onChange={handleCityChange}
                        description={
                          cityLoading
                            ? "Loading..."
                            : !formData.state
                              ? "Please Select State First"
                              : ""
                        }
                      />

                      {/* ZIP Code  */}
                      <div>
                        <p className="font-normal text-sm pb-1">ZIP Code </p>
                        <TextInput
                          label="Enter a ZIP Code"
                          type="text"
                          name="zipCode"
                          value={formData?.zipCode}
                          onChange={onChange}
                        />
                      </div>
                      {/* Intrest  */}
                      <div>
                        <p className="font-normal text-sm pb-1">Interests </p>
                        <div className="flex flex-wrap gap-2">
                          {interests?.map((item) => {
                            const isChecked = formData?.interests.includes(
                              item?.name,
                            );
                            return (
                              <SignupInterestCheckbox
                                key={item?.id}
                                name={item?.name}
                                value={isChecked}
                                onChange={() =>
                                  handleInterestsChange(item?.name)
                                }
                              >
                                {item?.label}
                              </SignupInterestCheckbox>
                            );
                          })}
                        </div>
                      </div>

                      {/* profession  */}
                      <div>
                        <p className="font-normal text-sm pb-1">Profession </p>
                        <TextInput
                          label="Enter a profession"
                          type="text"
                          name="profession"
                          value={formData?.profession}
                          onChange={onChange}
                        />
                      </div>

                      <div>
                        <p className="font-normal text-sm pb-1">Industry </p>
                        <TextInput
                          label="Enter an industry"
                          type="text"
                          name="industry"
                          value={formData?.industry}
                          onChange={onChange}
                        />
                      </div>

                      {/* Skill  */}
                      <div>
                        <p className="font-normal text-sm pb-1">Skill </p>
                        <TextInput
                          label="Enter a Skill"
                          type="text"
                          name="skill"
                          value={formData?.skill}
                          onChange={onChange}
                        />
                      </div>
                    </div>
                  </div>
                  <p
                    className={`text-sm pb-2 font-medium transition-colors duration-200 ease-in-out ${
                      isDisabled
                        ? "text-mediumgrey cursor-not-allowed"
                        : "text-secondary-dark hover:text-secondary-dark hover:underline cursor-pointer"
                    }`}
                    onClick={!isDisabled ? filterOnClick : undefined}
                  >
                    Apply Filters
                  </p>
                </div>
              </CustomPopover>
            )}
          </div>
          <div className="px-2">
            <Divider />
          </div>
          <div className="flex flex-col base:flex-row mx-1 base:justify-between base:items-center">
            {formData?.search ? (
              ""
            ) : (
              <div className="px-5 py-3 flex items-center gap-2 overflow-x-auto lg:flex-wrap no-scrollbar">
                {NETWORK_TABS.map((tab) => (
                  <Link key={tab.path} to={tab.path} className="shrink-0">
                    <Chip
                      cross={false}
                      variant={
                        location?.pathname === tab.path ? "filled" : "outlined"
                      }
                      text={tab.label}
                    />
                  </Link>
                ))}
              </div>
            )}
            <div
              className={`w-full py-3 mx-auto sm:mx-0 ${
                formData?.search ? "py-3" : "base:w-[300px]"
              }`}
            >
              <TextInput
                label="Search"
                type="text"
                name="search"
                value={formData?.search}
                onChange={onChange}
              />
            </div>
          </div>
        </div>
      </div>
      {hasActiveFilters ? (
        <>
          {partnersLoading ? (
            <PartnerCardSkeletonGrid count={12} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 base:grid-cols-3 gap-4 pb-6 mt-6">
              {partnersListing?.length > 0 ? (
                partnersListing.map((item) => (
                  <PartnerCard
                    key={item?.id}
                    src={item?.profile_image}
                    name={getFullName(item)}
                    designation={item?.profession}
                    address={getPartnerLocationLabel(item)}
                    id={item?.id}
                    handleClearFilter={handleClearFilter}
                    item={item}
                    pending={
                      item?.status === "MATCH" &&
                      item?.request_status === "PENDING" &&
                      item?.type === "pending"
                    }
                  />
                ))
              ) : (
                <EmptyState compact icon={HiOutlineUserGroup} title="No connections yet" description="Discover new connections." className="my-6" />
              )}
            </div>
          )}
        </>
      ) : (
        <Outlet />
      )}
    </div>
  );
};

export default Partner;
